import { Link } from "@tanstack/react-router";
import { CloudIcon, LaptopIcon, LoaderCircleIcon, MonitorIcon, PlayIcon } from "lucide-react";
import type {
  CmmdTaskExecutionOption,
  ModelSelection,
  ProviderKind,
  ServerProvider,
} from "@cmmd-forge/contracts";

import { ComposerFooterModeControls } from "~/components/chat/ComposerFooterModeControls";
import { ProviderModelPicker } from "~/components/chat/ProviderModelPicker";
import { buildComposerModelOptionsByProvider } from "~/components/chat/ChatComposer.logic";
import { BranchChip, WorktreeChip } from "~/components/newThread/composerChips";
import { Button } from "~/components/ui/button";
import { Skeleton } from "~/components/ui/skeleton";
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPanel,
  DialogPopup,
  DialogTitle,
} from "~/components/ui/dialog";
import { useCmmdForgeRuntimeListSnapshot } from "~/cmmd/cmmdForgeRuntimeListResource";
import { getCurrentForgeWorkspaceId } from "~/workspace/currentForgeWorkspaceStore";
import {
  resolveMissingForgeCloudServerState,
  TASK_WORK_MISSING_FORGE_CLOUD_SERVER_MESSAGE,
} from "~/lib/missingForgeCloudServer";
import { cn } from "~/lib/utils";
import { formatProductWorkspacePath } from "~/productSafeLabels";
import { getDefaultServerModel, resolveSelectableProvider } from "~/providerModels";
import type { TaskWorkSessionLauncher } from "./useTaskWorkSessionLauncher";
import { isReadyTaskWorkTarget, type TaskWorkTarget } from "./useTaskWorkSessionLauncher.logic";

export function TaskWorkSessionDialog(props: { readonly launcher: TaskWorkSessionLauncher }) {
  const launcher = props.launcher;
  const selectedTarget =
    launcher.targets.find((target) => target.key === launcher.selectedTargetKey) ?? null;
  const existingThread = launcher.existingConversation?.kind === "thread";
  const canStart =
    existingThread ||
    (selectedTarget !== null &&
      isReadyTaskWorkTarget(selectedTarget) &&
      launcher.canStartNewConversation);
  const displayedError = launcher.error ?? launcher.startUnavailableReason;

  return (
    <Dialog
      open={launcher.open}
      onOpenChange={(open) => {
        if (!open) launcher.close();
      }}
    >
      <DialogPopup className="max-w-2xl" showCloseButton={!launcher.starting}>
        <DialogHeader>
          <DialogTitle>Start task</DialogTitle>
          <DialogDescription>{taskDialogDescription(launcher)}</DialogDescription>
        </DialogHeader>
        <DialogPanel className="space-y-4">
          {launcher.existingConversation !== null ? (
            <ExistingTaskConversationNotice conversation={launcher.existingConversation} />
          ) : (
            <>
              <TaskWorkTargetList launcher={launcher} />
              <TaskThreadConfiguration launcher={launcher} />
            </>
          )}
          {displayedError ? (
            <div
              role="alert"
              className="rounded-lg border border-destructive-border bg-destructive-surface px-3 py-2 text-sm text-destructive"
            >
              {displayedError}
            </div>
          ) : null}
        </DialogPanel>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            disabled={launcher.starting}
            onClick={launcher.close}
          >
            Cancel
          </Button>
          <Button
            type="button"
            disabled={!canStart || launcher.loading || launcher.starting}
            onClick={() => void launcher.start()}
          >
            {launcher.starting ? (
              <LoaderCircleIcon className="animate-spin" aria-hidden />
            ) : (
              <PlayIcon aria-hidden />
            )}
            {taskDialogActionLabel(launcher)}
          </Button>
        </DialogFooter>
      </DialogPopup>
    </Dialog>
  );
}

function taskDialogDescription(launcher: TaskWorkSessionLauncher): string {
  const taskTitle = launcher.task?.title;
  if (launcher.existingConversation?.kind === "thread") {
    return `“${taskTitle ?? "This task"}” already has one durable root conversation. Open it to continue or retry in the same history. This never creates an Automation run.`;
  }
  if (launcher.existingConversation?.kind === "starting") {
    return `“${taskTitle ?? "This task"}” is already starting in another Forge session. Wait for its root conversation instead of launching a duplicate.`;
  }
  return `Configure “${taskTitle ?? "this task"}” like a new Forge conversation. Forge rechecks for an existing root conversation immediately before creating one task-linked work session. This never creates an Automation run.`;
}

function taskDialogActionLabel(launcher: TaskWorkSessionLauncher): string {
  if (launcher.starting) return "Starting…";
  if (launcher.existingConversation?.kind === "thread") return "Open task conversation";
  if (launcher.existingConversation?.kind === "starting") return "Task is starting";
  return "Start task";
}

function ExistingTaskConversationNotice(props: {
  readonly conversation: NonNullable<TaskWorkSessionLauncher["existingConversation"]>;
}) {
  return (
    <div className="rounded-xl border border-border bg-card px-4 py-3">
      <p className="text-sm font-medium text-foreground">
        {props.conversation.kind === "thread"
          ? "Durable task conversation found"
          : "Task launch already in progress"}
      </p>
      <p className="mt-1 text-xs text-muted-foreground">
        {props.conversation.kind === "thread"
          ? "Forge will open the existing task-anchored conversation. Its goal, messages, and retry history stay together."
          : "Forge will not create another work session while the existing launch is still attaching its root conversation."}
      </p>
    </div>
  );
}

function TaskThreadConfiguration(props: { readonly launcher: TaskWorkSessionLauncher }) {
  const launcher = props.launcher;
  const { provider, model } = resolveTaskThreadProviderModel({
    providers: launcher.serverProviders,
    selection: launcher.configuration.modelSelection,
  });
  const modelOptionsByProvider = buildComposerModelOptionsByProvider(launcher.serverProviders);

  return (
    <section aria-labelledby="task-thread-configuration-heading" className="space-y-3">
      <div>
        <h3 id="task-thread-configuration-heading" className="text-sm font-medium text-foreground">
          Conversation setup
        </h3>
        <p className="mt-0.5 text-xs text-muted-foreground">
          The selected task, project, goal, and agent identity stay locked to this conversation.
        </p>
      </div>
      <div className="flex flex-wrap items-center gap-1 rounded-xl border border-border bg-card px-2 py-2">
        <ProviderModelPicker
          compact={false}
          provider={provider}
          model={model}
          lockedProvider={null}
          providers={launcher.serverProviders}
          providerAvailability={launcher.providerAvailability}
          modelOptionsByProvider={modelOptionsByProvider}
          orgProviderPolicy={launcher.orgProviderPolicy}
          onProviderModelChange={launcher.setModelSelection}
        />
        <ComposerFooterModeControls
          interactionMode={launcher.configuration.interactionMode}
          runtimeMode={launcher.configuration.runtimeMode}
          onToggleInteractionMode={() =>
            launcher.setInteractionMode(
              launcher.configuration.interactionMode === "plan" ? "default" : "plan",
            )
          }
          onRuntimeModeChange={launcher.setRuntimeMode}
        />
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <WorktreeChip
          mode={launcher.configuration.environmentMode}
          onSelectMode={(mode) => {
            if (mode === "local" || mode === "worktree") {
              launcher.setEnvironmentMode(mode);
            }
          }}
        />
        {launcher.configuration.environmentMode === "worktree" ? (
          <BranchChip
            branch={launcher.configuration.baseBranch ?? "main"}
            knownBranches={[launcher.configuration.baseBranch ?? "main"]}
            onSelectBranch={launcher.setBaseBranch}
          />
        ) : null}
      </div>
    </section>
  );
}

function resolveTaskThreadProviderModel(input: {
  readonly providers: readonly ServerProvider[];
  readonly selection: ModelSelection | null;
}): { readonly provider: ProviderKind; readonly model: string } {
  const selectedProviderAvailable = input.providers.some(
    (candidate) => candidate.provider === input.selection?.provider,
  );
  if (selectedProviderAvailable && input.selection !== null) {
    return {
      provider: input.selection.provider,
      model:
        input.selection.model || getDefaultServerModel(input.providers, input.selection.provider),
    };
  }
  // `resolveSelectableProvider` rather than the default resolver, because this
  // has to name a provider for the picker to render even when the target
  // runtime has published nothing selectable. The launch itself resolves its
  // model against the live inventory, so nothing dispatches on this answer.
  const provider = resolveSelectableProvider(input.providers, null);
  return { provider, model: getDefaultServerModel(input.providers, provider) };
}

function TaskWorkTargetList(props: { readonly launcher: TaskWorkSessionLauncher }) {
  const cloudCatalog = useCmmdForgeRuntimeListSnapshot();
  const missingForgeCloudServer =
    resolveMissingForgeCloudServerState(cloudCatalog, getCurrentForgeWorkspaceId()) === "missing";

  if (props.launcher.loading) {
    return (
      <div aria-label="Loading runtimes" className="space-y-2">
        {[0, 1, 2].map((index) => (
          <Skeleton key={index} className="h-16 rounded-xl border border-border" />
        ))}
      </div>
    );
  }
  if (props.launcher.targets.length > 0) {
    return (
      <div role="radiogroup" aria-label="Runtime target" className="space-y-2">
        {props.launcher.targets.map((target) => (
          <TaskWorkTargetRow
            key={target.key}
            target={target}
            selected={target.key === props.launcher.selectedTargetKey}
            onSelect={() => props.launcher.selectTarget(target.key)}
          />
        ))}
      </div>
    );
  }
  if (props.launcher.options.length > 0) {
    return (
      <div className="space-y-2">
        {props.launcher.options.map((option) => (
          <UnavailableRuntimeRow key={option.runtime.id} option={option} />
        ))}
      </div>
    );
  }
  if (missingForgeCloudServer) {
    return (
      <div
        className="rounded-xl border border-dashed border-border px-4 py-6 text-center"
        data-testid="task-work-setup"
        data-state="confirm"
      >
        <p className="text-sm text-muted-foreground">
          {TASK_WORK_MISSING_FORGE_CLOUD_SERVER_MESSAGE}
        </p>
        <Button type="button" className="mt-4" render={<Link to="/setup" />}>
          Setup
        </Button>
      </div>
    );
  }
  return (
    <p className="rounded-xl border border-dashed border-border px-4 py-6 text-center text-sm text-muted-foreground">
      No authorized runtime targets were returned.
    </p>
  );
}

function TaskWorkTargetRow(props: {
  readonly target: TaskWorkTarget;
  readonly selected: boolean;
  readonly onSelect: () => void;
}) {
  const ready = isReadyTaskWorkTarget(props.target);
  const RuntimeIcon = runtimeIcon(props.target.runtime.source, props.target.runtime.runtimeKind);
  const projectLabel =
    props.target.githubRepoFullName ??
    formatProductWorkspacePath(props.target.workspaceRoot, "safe");
  return (
    <button
      type="button"
      role="radio"
      aria-checked={props.selected}
      disabled={!ready}
      onClick={props.onSelect}
      className={cn(
        "flex min-h-16 w-full items-center gap-3 rounded-xl border px-3 py-2.5 text-left transition-colors",
        props.selected
          ? "border-primary bg-primary/8 ring-1 ring-primary/30"
          : "border-border bg-card hover:bg-muted",
        ready ? "text-foreground" : "cursor-not-allowed opacity-55",
      )}
    >
      <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
        <RuntimeIcon className="size-4.5" aria-hidden />
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex min-w-0 items-center gap-2">
          <span className="truncate text-sm font-medium">{props.target.runtime.displayName}</span>
          <RuntimeAvailability availability={props.target.availability} />
        </span>
        <span className="mt-0.5 block truncate text-xs text-muted-foreground">{projectLabel}</span>
        {!ready && props.target.unavailableReason ? (
          <span className="mt-0.5 block truncate text-xs text-destructive">
            {props.target.unavailableReason}
          </span>
        ) : null}
      </span>
      <span
        aria-hidden
        className={cn(
          "size-4 rounded-full border",
          props.selected ? "border-[5px] border-primary" : "border-border",
        )}
      />
    </button>
  );
}

export function resolveUnavailableRuntimeReason(
  option: Pick<CmmdTaskExecutionOption, "requiresProjectLink" | "unavailableReason">,
): string {
  return (
    option.unavailableReason ??
    (option.requiresProjectLink
      ? "Link this CMMD project to a Forge project here."
      : "No Forge project is available here.")
  );
}

function UnavailableRuntimeRow(props: { readonly option: CmmdTaskExecutionOption }) {
  const RuntimeIcon = runtimeIcon(props.option.runtime.source, props.option.runtime.runtimeKind);
  return (
    <div className="flex min-h-16 items-center gap-3 rounded-xl border border-border bg-muted px-3 py-2.5 opacity-70">
      <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
        <RuntimeIcon className="size-4.5" aria-hidden />
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex min-w-0 items-center gap-2">
          <span className="truncate text-sm font-medium text-foreground">
            {props.option.runtime.displayName}
          </span>
          <RuntimeAvailability availability={props.option.availability} />
        </span>
        <span className="mt-0.5 block text-xs text-muted-foreground">
          {resolveUnavailableRuntimeReason(props.option)}
        </span>
      </span>
    </div>
  );
}

function RuntimeAvailability(props: {
  readonly availability: CmmdTaskExecutionOption["availability"];
}) {
  return (
    <span
      className={cn(
        "shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-medium capitalize",
        props.availability === "ready"
          ? "bg-success-surface text-success-foreground"
          : "bg-muted text-muted-foreground",
      )}
    >
      {props.availability.replace("_", " ")}
    </span>
  );
}

function runtimeIcon(
  source: CmmdTaskExecutionOption["runtime"]["source"],
  kind: string,
): typeof MonitorIcon {
  if (source === "forge-cloud") return CloudIcon;
  return kind.toLowerCase().includes("desktop") ? LaptopIcon : MonitorIcon;
}
